import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import useTypewriterPlaceholder from "../../hooks/useTypewriterPlaceholder";
import FairQuoteCard from "../trust/FairQuoteCard";
import TrustScorePromoSection from "../trust/TrustScorePromoSection";
import "../../styles/Home.css";

/* The demo prompt shown (and sent, if the visitor just taps the arrow) by
   the input on the garden render. Single phrase so what you see is always
   what the send button sends. */
const DESIGN_PROMPT_DEFAULT = "Design a new sandstone patio";
const DESIGN_PROMPT_PHRASES = [DESIGN_PROMPT_DEFAULT];

const CheckBullet = ({ children }) => (
  <li className="flex items-start gap-3 text-gray-700 font-medium text-base md:text-lg feature-bullet">
    <span className="w-7 h-7 rounded-full bg-yellow-50 text-[#FFBD59] flex items-center justify-center shrink-0 mt-0.5 border border-yellow-100">
      <svg
        className="w-4 h-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
    </span>
    {children}
  </li>
);

// Tom's three full-width feature sections (HomeTest.html), stacked on
// desktop AND mobile — text one side, visual the other on large screens.
const FeatureSections = ({ onChatClick, onDesignSubmit }) => {
  const navigate = useNavigate();
  const [trade, setTrade] = useState("");
  const [searchArea, setSearchArea] = useState("");
  const [designMsg, setDesignMsg] = useState("");

  const {
    displayText: designTyped,
    isAnimating: designAnimating,
    onFocus: designTwFocus,
    onBlur: designTwBlur,
    setUserActive: setDesignTwActive,
  } = useTypewriterPlaceholder(DESIGN_PROMPT_PHRASES);

  useEffect(() => {
    if (designMsg.length > 0) {
      setDesignTwActive(true);
    }
  }, [designMsg, setDesignTwActive]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({ source: "homepage_features" });
    if (trade.trim()) params.set("search", trade.trim());
    if (searchArea.trim()) params.set("location", searchArea.trim());
    navigate(`/local-contractors?${params.toString()}`);
  };

  const handleDesignSubmit = (e) => {
    e.preventDefault();
    if (!onDesignSubmit) return;
    onDesignSubmit(designMsg.trim() || DESIGN_PROMPT_DEFAULT);
  };

  return (
    <div className="section home-features flex flex-col gap-4 md:gap-6">
      {/* 1 — Chat with Percy (text left, chat visual right) */}
      <section className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-6 md:p-10 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="order-2 lg:order-1 flex flex-col justify-center min-w-0">
            <h2 className="feature-title text-3xl md:text-4xl font-extrabold text-[#1A1D23] mb-4 leading-tight tracking-tight">
              Chat with Percy to{" "}
              <span className="highlight-text">plan your budget.</span>
            </h2>
            <p className="feature-copy text-base md:text-lg text-gray-600 leading-relaxed mb-6">
              Chat with Percy to outline your job and explore a modelled budget.
              Clarify quantities, materials and assumptions, then discuss your
              project with a tradesperson when you're ready.
            </p>
            <ul className="space-y-4 mb-7">
              <CheckBullet>
                Explore indicative estimates with regional model assumptions.
              </CheckBullet>
              <CheckBullet>
                Prepare a clearer brief before requesting written quotes.
              </CheckBullet>
              <CheckBullet>
                Review profiles and contact tradespeople when you choose.
              </CheckBullet>
            </ul>
            <button
              type="button"
              onClick={onChatClick}
              className="inline-flex justify-center items-center bg-[#E8940A] hover:bg-[#D17D00] text-white px-8 py-3.5 rounded-xl font-bold text-base md:text-lg w-full sm:w-auto transition-colors"
            >
              Start Chat
            </button>
          </div>

          <div className="order-1 lg:order-2 w-full flex justify-center lg:justify-end min-w-0">
            <div className="relative w-full max-w-[420px] mb-12" aria-hidden="true">
              {/* Hanging price tag */}
              <div className="absolute -right-2 sm:-right-4 -bottom-12 z-30 transform rotate-12 origin-top drop-shadow-xl">
                <div className="absolute -top-4 left-1/2 w-0.5 h-6 bg-gray-300 transform -translate-x-1/2"></div>
                <div className="absolute -top-5 left-1/2 w-3 h-3 bg-gray-400 rounded-full transform -translate-x-1/2 shadow-sm border border-gray-300"></div>
                <div className="bg-white border-2 border-[#FFBD59] rounded-xl shadow-lg p-3 sm:p-4 w-40 sm:w-44 text-center relative">
                  <div className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-50 rounded-full border border-gray-200 shadow-inner"></div>
                  <p className="mt-2 text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-widest">
                    Est. Cost
                  </p>
                  <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5 whitespace-nowrap">
                    £4.2k - £5.5k
                  </p>
                </div>
              </div>

              {/* Chat container */}
              <div className="w-full bg-gray-50 rounded-[28px] border border-gray-200 shadow-inner p-5 sm:p-6 flex flex-col gap-4 relative z-10">
                <div className="flex flex-col gap-3 sm:gap-4 w-full pt-2">
                  <div className="self-end bg-[#FFBD59] text-[#111827] font-medium py-2.5 px-4 rounded-2xl rounded-br-sm max-w-[85%] text-[14px] sm:text-[15px] leading-snug shadow-sm">
                    How much for a new patio?
                  </div>
                  <div className="self-start text-[#6b7280] text-[14px] sm:text-[15px] italic font-medium px-3 -my-1">
                    Splendid, how big is the area roughly?
                  </div>
                  <div className="self-end bg-[#FFBD59] text-[#111827] font-medium py-2.5 px-4 rounded-2xl rounded-br-sm max-w-[85%] text-[14px] sm:text-[15px] leading-snug shadow-sm">
                    35 sq metres
                  </div>
                  <div className="self-start text-[#6b7280] text-[14px] sm:text-[15px] italic font-medium px-3 -my-1">
                    Perfect, what material would you like?
                  </div>
                  <div className="self-end bg-[#FFBD59] text-[#111827] font-medium py-2.5 px-4 rounded-2xl rounded-br-sm max-w-[85%] text-[14px] sm:text-[15px] leading-snug shadow-sm">
                    Indian Sandstone
                  </div>
                  <div className="self-start text-[#6b7280] text-[14px] sm:text-[15px] italic font-medium px-3 -my-1">
                    When would you like it done?
                  </div>
                </div>

                {/* Chat input bar */}
                <div className="mt-2 w-full bg-white border border-gray-200 rounded-full p-2 pl-5 flex items-center shadow-sm">
                  <div className="flex items-center flex-grow overflow-hidden text-[#111827]">
                    <span className="font-medium text-[14px] sm:text-[15px] whitespace-nowrap">
                      In August please
                    </span>
                    <span className="inline-block w-[2px] h-[18px] bg-[#111827] ml-1 animate-pulse rounded-full"></span>
                  </div>
                  <span className="ml-3 w-9 h-9 sm:w-10 sm:h-10 bg-[#FFBD59] rounded-full flex items-center justify-center shrink-0 shadow-sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2.5"
                      stroke="currentColor"
                      className="w-4 h-4 sm:w-5 sm:h-5 text-gray-900"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
                      />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 — See your new space (image left, text right) */}
      <section className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-6 md:p-10 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="order-1 w-full flex justify-center lg:justify-start min-w-0">
            <div className="relative w-full max-w-[400px] aspect-[3/4] rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <img
                src="/images/home-garden.webp"
                alt="AI render of a new sandstone garden patio"
                width="672"
                height="900"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              {/* Working chat input on the render: demo-types the prompt, and
                  sending it (typed or as-is) starts the real Percy chat with
                  that message (?q= auto-sends, same as the hero input). */}
              <form
                onSubmit={handleDesignSubmit}
                className="absolute bottom-2.5 left-2.5 right-2.5 sm:bottom-4 sm:left-4 sm:right-4 bg-white/95 backdrop-blur-sm border border-gray-200 rounded-full p-1.5 pl-4 sm:pl-5 flex items-center shadow-lg"
              >
                <div className="relative flex-1 min-w-0 flex items-center overflow-hidden">
                  <input
                    type="text"
                    value={designMsg}
                    onChange={(e) => setDesignMsg(e.target.value)}
                    onFocus={designTwFocus}
                    onBlur={() => {
                      if (!designMsg.trim()) designTwBlur();
                    }}
                    aria-label="Tell Percy what you'd like to change about your space"
                    className="w-full min-w-0 bg-transparent text-[16px] font-medium text-gray-900 focus:outline-none"
                    placeholder=""
                  />
                  {designAnimating && !designMsg && (
                    <span
                      className="absolute inset-y-0 left-0 flex items-center text-[15px] sm:text-[16px] font-medium text-gray-600 pointer-events-none whitespace-nowrap"
                      aria-hidden="true"
                    >
                      {designTyped}
                      <span className="inline-block w-[2px] h-[16px] sm:h-[18px] bg-gray-500 ml-0.5 animate-pulse rounded-full"></span>
                    </span>
                  )}
                </div>
                <button
                  type="submit"
                  aria-label="Send this to the Percy chat"
                  className="ml-2 sm:ml-3 w-8 h-8 sm:w-9 sm:h-9 bg-[#FFBD59] hover:bg-[#f0a93d] rounded-full flex items-center justify-center shrink-0 shadow-sm transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2.5"
                    stroke="currentColor"
                    className="w-4 h-4 text-gray-900"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
                    />
                  </svg>
                </button>
              </form>
            </div>
          </div>

          <div className="order-2 flex flex-col justify-center min-w-0">
            <h2 className="feature-title text-3xl md:text-4xl font-extrabold text-[#1A1D23] mb-4 leading-tight tracking-tight">
              Picture your new{" "}
              <span className="highlight-text">space with AI.</span>
            </h2>
            <p className="feature-copy text-base md:text-lg text-gray-600 leading-relaxed mb-7">
              Upload a photo and describe your ideas to explore an AI-generated
              design. Confirm the real measurements, then use an indicative
              estimate to help plan your budget.
            </p>
            <button
              type="button"
              onClick={onChatClick}
              className="inline-flex justify-center items-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-xl font-bold text-base md:text-lg w-full sm:w-auto transition-colors"
            >
              Design Project
            </button>
          </div>
        </div>
      </section>

      {/* 3 — Find tradespeople (text + working search left, image right) */}
      <section className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-6 md:p-10 lg:p-12 relative overflow-hidden">
        {/* Soft background decoration */}
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-yellow-50 rounded-full blur-3xl opacity-60 pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-60 pointer-events-none"></div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="relative z-10 order-2 lg:order-1 text-center lg:text-left min-w-0">
            <h2 className="feature-title text-3xl md:text-4xl font-extrabold text-[#1A1D23] mb-4 leading-tight tracking-tight">
              Find tradespeople{" "}
              <span className="highlight-text">near you</span>
            </h2>
            <p className="feature-copy text-base md:text-lg text-gray-600 leading-relaxed mb-7 max-w-xl mx-auto lg:mx-0">
              Explore tradesperson profiles and the evidence shown on each one.
              Check availability in your area and discuss your project directly.
            </p>

            <form
              onSubmit={handleSearchSubmit}
              className="w-full bg-gray-50 p-4 rounded-3xl flex flex-col gap-3 shadow-inner border border-gray-100 max-w-xl mx-auto lg:mx-0"
            >
              <div className="flex items-center bg-white rounded-xl border border-gray-200 focus-within:border-[#FFBD59] transition-colors">
                <span className="pl-4 text-gray-400">
                  <i className="fas fa-search" aria-hidden="true" />
                </span>
                <input
                  type="text"
                  value={trade}
                  onChange={(e) => setTrade(e.target.value)}
                  placeholder="e.g. Plumber, Landscaper..."
                  aria-label="Trade or job type"
                  className="w-full min-w-0 bg-transparent text-[16px] font-medium text-gray-900 placeholder-gray-400 px-4 py-3.5 focus:outline-none rounded-xl"
                />
              </div>
              <div className="flex items-center bg-white rounded-xl border border-gray-200 focus-within:border-[#FFBD59] transition-colors">
                <span className="pl-4 text-gray-400">
                  <i className="fas fa-map-marker-alt" aria-hidden="true" />
                </span>
                <input
                  type="text"
                  value={searchArea}
                  onChange={(e) => setSearchArea(e.target.value)}
                  placeholder="Postcode or town"
                  aria-label="Postcode or town"
                  className="w-full min-w-0 bg-transparent text-[16px] font-medium text-gray-900 placeholder-gray-400 px-4 py-3.5 focus:outline-none rounded-xl"
                />
              </div>
              <button
                type="submit"
                className="bg-[#111827] hover:bg-gray-800 text-white font-bold rounded-xl px-6 py-3.5 text-base md:text-lg transition-colors mt-1"
              >
                Search Now
              </button>
            </form>
          </div>

          <div className="w-full flex justify-center lg:justify-end order-1 lg:order-2 min-w-0">
            <img
              src="/images/home-tradies.webp"
              alt="Local penguin tradespeople ready to work"
              width="1024"
              height="1024"
              loading="lazy"
              className="w-full max-w-[440px] h-auto rounded-3xl"
            />
          </div>
        </div>
      </section>

      {/* 4 — Quote Check promo (text left, FAIR quote right). Meets people
          in their existing habit (Tom, 6/8): get the quote first, check it
          after — rather than asking them to change how they buy. */}
      <section className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-6 md:p-10 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="order-2 lg:order-1 flex flex-col justify-center min-w-0">
            <h2 className="feature-title text-3xl md:text-4xl font-extrabold text-[#1A1D23] mb-4 leading-tight tracking-tight">
              Already got a quote?{" "}
              <span className="highlight-text">See how it compares.</span>
            </h2>
            <p className="feature-copy text-base md:text-lg text-gray-600 leading-relaxed mb-6">
              Paste it or snap a photo for an indicative comparison with
              modelled allowances. Percy highlights missing scope and useful
              questions to ask before agreeing the work.
            </p>
            <ul className="space-y-4 mb-7">
              <CheckBullet>
                Works with a photo of a paper quote or pasted text.
              </CheckBullet>
              <CheckBullet>
                Compare supported job scope with a modelled planning range.
              </CheckBullet>
              <CheckBullet>
                Get the questions worth asking before you say yes.
              </CheckBullet>
            </ul>
            <Link
              to="/quote-check"
              className="inline-flex justify-center items-center bg-[#E8940A] hover:bg-[#D17D00] text-white px-8 py-3.5 rounded-xl font-bold text-base md:text-lg w-full sm:w-auto transition-colors"
            >
              Check My Quote — Free
            </Link>
          </div>

          <div className="order-1 lg:order-2 w-full flex justify-center lg:justify-end min-w-0">
            <FairQuoteCard />
          </div>
        </div>
      </section>

      {/* 5 — Trusted by Percy (badge left, text right). Homeowner-framed:
          one concept — the badge — with the detail behind the link. */}
      <section className="feature-panel bg-white border border-[#E8E8EC] rounded-[22px] p-6 md:p-10 lg:p-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          <div className="order-1 w-full flex justify-center lg:justify-start min-w-0">
            <img
              src="/images/trusted-by-percy-shield.webp"
              alt="Trusted by Percy — Pricing Penguin Accreditation badge"
              width="1080"
              height="1080"
              loading="lazy"
              className="w-full max-w-[420px] h-auto object-contain"
            />
          </div>

          <div className="order-2 flex flex-col justify-center min-w-0">
            <h2 className="feature-title text-3xl md:text-4xl font-extrabold text-[#1A1D23] mb-4 leading-tight tracking-tight">
              What is{" "}
              <span className="highlight-text">Trusted by Percy?</span>
            </h2>
            <p className="feature-copy text-base md:text-lg text-gray-600 leading-relaxed mb-7">
              Pricing Penguin's own review scheme for tradespeople. It uses
              submitted evidence and modelled pricing comparisons. Check each
              badge's current status, scope and date; it is not external
              certification or a guarantee of price or workmanship.
            </p>
            <Link
              to="/trusted-by-percy"
              className="inline-flex justify-center items-center bg-[#E8940A] hover:bg-[#D17D00] text-white px-8 py-3.5 rounded-xl font-bold text-base md:text-lg w-full sm:w-auto transition-colors"
            >
              See How It Works
            </Link>
          </div>
        </div>
      </section>

      {/* 6 — Trust Score promo (dial left, text right) — shared with
          /contractor-packages. */}
      <TrustScorePromoSection variant="home" />
    </div>
  );
};

export default FeatureSections;
